import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
} from '@angular/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import {
  BookingService,
  calculateDiscountPercent,
  EmployeeProcedureService,
  EmployeeService,
  secondsToTimeStr,
  ServiceService,
  timeStrToSeconds,
  RoomService,
  CheckAvailabilityRequest,
  AvailabilityResponse,
  AvailabilityConflict,
  AvailableOfferService,
  AvailablePackageService,
} from '@beautyapp/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe } from '@angular/common';
import { NzTableModule } from 'ng-zorro-antd/table';
import { TranslateModule } from '@ngx-translate/core';
import {
  canCheckAvailability,
  isEmployeePickerLocked,
  resolveHeaderEmployee,
} from './add-booking.helpers';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import {
  InputModule,
  TimePickerComponent,
  TextareaComponent,
  SelectComponent,
} from '@beautyapp/form';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ChooseServiceComponent } from '../choose-service/choose-service.component';
import { addMinutes, format } from 'date-fns';
import { VisitAddFacade } from '@beautyapp/shared';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  forkJoin,
  lastValueFrom,
  of,
  throwError,
} from 'rxjs';
import {
  AppointmentProductsComponent,
  AppointmentSpecServicesComponent,
} from '../../../booking-modal/components';
import { map, switchMap, tap, defaultIfEmpty } from 'rxjs/operators';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { AddVisitBooking } from '../../models/add-visit-booking';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { createDateRangeValidator } from '../../../validators/appointment.validator';
import {
  seedAutoFixExpense,
  shouldTrackProductExpense,
} from '../seed-auto-fix-expense';


/**
 * Custom validator to ensure time value is greater than zero
 * @param control Form control to validate
 * @returns Validation error object or null if valid
 */
function ValidateTime(control: AbstractControl): { [key: string]: any } | null {
  if (control.value && timeStrToSeconds(control.value) <= 0) {
    return { minTimeRequired: true };
  }
  return null;
}

/**
 * Component for adding or editing a booking
 *
 * This component handles:
 * - Service selection
 * - Booking details entry (time, price, employee, etc.)
 * - Service products management
 * - Form validation and submission
 */
@UntilDestroy()
@Component({
  selector: 'beautyapp-add-booking',
  standalone: true,
  imports: [
    NzModalModule,
    NzTableModule,
    TranslateModule,
    NzSpaceModule,
    NzIconDirective,
    NzButtonComponent,
    InputModule,
    ReactiveFormsModule,
    FormsModule,
    ChooseServiceComponent,
    AsyncPipe,
    NzCheckboxComponent,
    AppointmentProductsComponent,
    AppointmentSpecServicesComponent,
    NgxPermissionsModule,
    NzCardComponent,
    NzSelectModule,
    NzInputNumberModule,
    NzAlertModule,
    TimePickerComponent,
    TextareaComponent,
    SelectComponent,
    NzTagModule,
  ],
  templateUrl: './add-booking.component.html',
  styleUrl: './add-booking.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddBookingComponent implements OnInit {
  data = inject(NZ_MODAL_DATA);
  modalRef = inject(NzModalRef);
  serviceService: ServiceService = inject(ServiceService);
  employeeServiceService: EmployeeProcedureService = inject(
    EmployeeProcedureService
  );
  visitAddFacade = inject(VisitAddFacade);
  bookingService = inject(BookingService);
  employeeService = inject(EmployeeService);
  roomService = inject(RoomService);
  permissionsService = inject(NgxPermissionsService);

  /** Flag to determine if service selection is needed */
  needChooseService = signal(!this.data.booking);

  /** Currently selected service */
  service = signal<any | null>(null);

  /** Products associated with the service */
  serviceProducts = signal<any[]>([]);

  /** Special services associated with the booking */
  serviceSpecs = signal<any[]>([]);

  /** Loading state for async operations */
  loading = signal(false);

  /** Flag to track if form has been initialized */
  isFormInitialized = signal(false);

  /** Available rooms for the selected date */
  availableRooms = signal<any[]>([]);

  /** Availability check signals */
  availabilityConflicts = signal<AvailabilityConflict[]>([]);
  isCheckingAvailability = signal(false);
  hasAvailabilityConflicts = computed(
    () => this.availabilityConflicts().length > 0
  );
  /** Flag to track if user has seen the warning and wants to proceed */
  proceedDespiteConflicts = signal(false);

  employeeId = signal<string | null>(this.data.employeeId || null);

  /**
   * beaut-545. The visit planning page has already settled who this booking is
   * for — one booking carries exactly one employee — so the picker below shows
   * that person read-only instead of offering a second, disagreeing choice.
   *
   * Only for a new booking opened WITH an employee. Opened without one (no
   * calendar column behind it) this select is the only place the choice can be
   * made, and editing an existing booking is a different operation the card
   * says nothing about; both keep their current behaviour.
   */
  employeeLocked = isEmployeePickerLocked({
    employeeId: this.data.employeeId,
    booking: this.data.booking,
    followUpBooking: this.data.followUpBooking,
  });
  employeeResource = rxResource({
    params: () => ({ employeeId: this.employeeId() }),
    stream: ({ params }) => {
      if (!params.employeeId) return of(null);
      return this.employeeService
        .getById(params.employeeId)
        .pipe(catchError(() => of(null)));
    },
  });

  employee = linkedSignal(() => this.employeeResource.value());

  /** beaut-545. The specialist shown above the panel. See resolveHeaderEmployee. */
  headerEmployee = computed(() =>
    resolveHeaderEmployee({
      loaded: this.employee(),
      currentEmployeeId: this.employeeId(),
      originalEmployeeId: this.data.employeeId ?? null,
      originalEmployee: this.data.employee ?? null,
    })
  );

  /** Additional employees for multi-employee rooms */
  additionalEmployees = signal<
    {
      employeeId: string;
      salaryFixed: number | null;
      salaryPercent: number | null;
    }[]
  >([]);

  selectedRoomId = signal<string | null>(null);

  selectedRoom = computed(() =>
    this.availableRooms().find((r) => r.id === this.selectedRoomId())
  );
  /**
   * Computed property to check if selected room allows multiple employees
   */
  selectedRoomAllowsMultipleEmployees = computed(() => {
    const roomId = this.selectedRoomId();
    if (!roomId) return false;
    const room = this.availableRooms().find((r) => r.id === roomId);
    console.log('Selected room:', room);
    return room?.canWorkMultipleEmployees || false;
  });

  /**
   * Checks if current user has Employee permission
   */
  get isEmployee() {
    return !this.permissionsService.getPermission('calendar.view_all');
  }

  /**
   * Gets the sale price from the form
   */
  get salePrice() {
    return this.form.get('salePrice')?.value;
  }

  /**
   * Gets the booking status from input data
   */
  get bookingStatus() {
    return this.data.bookingStatus;
  }

  /**
   * Determines if salary percent from price option should be enabled
   */
  get isEnableSalaryPercentFromPrice() {
    return (
      this.salePrice &&
      this.salePrice > 0 &&
      this.form.get('salaryPercent')?.value
    );
  }

  bookingId = signal<string | null>(this.data.booking?.id || null);

  /**
   * Resource for loading booking data
   */
  bookingResource = rxResource({
    params: () => ({ bookingId: this.bookingId() }),
    stream: ({ params }) =>
      params.bookingId
        ? this.bookingService
            .getById(params.bookingId)
            .pipe(catchError(() => of(null)))
        : of(null),
  });

  /**
   * Current booking data
   */
  booking = computed(() => this.bookingResource.value());

  /**
   * Form for booking data
   */
  form = new FormGroup(
    {
      visitId: new FormControl(this.data.visitId),
      userId: new FormControl(this.data.userId),
      bookingDate: new FormControl(this.data.bookingDate, Validators.required),
      startTime: new FormControl<Date>(
        new Date(this.data.startTime),
        Validators.required
      ),
      endTime: new FormControl<Date | null>(null, Validators.required),
      serviceId: new FormControl(null, Validators.required),
      time: new FormControl<string | null>(null, [
        Validators.required,
        ValidateTime,
      ]),
      price: new FormControl<number | null>(null, [Validators.required]),
      salePrice: new FormControl<number | null>(null),
      salePercent: new FormControl<number | null>(null),
      employeeId: new FormControl(this.data.employeeId, Validators.required),
      roomId: new FormControl<string | null>(null), // Room assignment (optional)
      salaryFixed: new FormControl(null),
      // beaut-424: a share of a price cannot exceed the price. The backend
      // rejects >100 with a 422; without this the operator only discovers that
      // at final save, as a status code instead of a message under the field.
      salaryPercent: new FormControl(null, [
        Validators.min(0),
        Validators.max(100),
      ]),
      description: new FormControl(null),
      // Product cost is seeded from the service products, the additional cost
      // from the procedure's fixSum. Both stay editable: a recipe states a
      // nominal amount, while what a procedure actually consumes varies.
      expense: new FormControl<number | null>(null),
      additionalExpense: new FormControl<number | null>(null),
      quantity: new FormControl(1),
      salaryPercentFromPrice: new FormControl(false),
      minute: new FormControl(0),
      sendNotification: new FormControl(true),
      additionalEmployees: new FormArray([]),
    },
    {
      validators: [createDateRangeValidator()],
    }
  );

  get additionalEmployeesFrom() {
    return this.form.get('additionalEmployees') as FormArray;
  }

  constructor() {
    effect(() => {
      if (this.booking()) {
        // Create a form value object to be patched
        const formValues: any = {
          visitId: this.booking().visitId,
          userId: this.booking().userId,
          bookingDate: this.booking().bookingDate,
          serviceId: this.booking().bookingService?.serviceId,
          price: this.booking().bookingService?.price,
          quantity: this.booking().bookingService?.quantity,
          salePrice:
            +this.booking().bookingService?.salePrice > 0
              ? +this.booking().bookingService?.salePrice
              : null,
          salePercent:
            +this.booking().bookingService?.salePrice > 0
              ? +calculateDiscountPercent(
                  this.booking().bookingService?.price,
                  +this.booking().bookingService.salePrice
                )
              : null,
          salaryFixed: this.booking().bookingService?.salaryFixed,
          salaryPercent: this.booking().bookingService?.salaryPercent,
          salaryPercentFromPrice:
            this.booking().bookingService?.salaryPercentFromPrice,
          description: this.booking().description,
          time: this.booking().bookingService?.time,
          minute: timeStrToSeconds(this.booking().bookingService?.time),
          expense: this.booking().bookingService?.expence,
          additionalExpense:
            this.booking().bookingService?.additionalExpence ?? 0,
          startTime: this.booking().startTimeDate,
          endTime: this.booking().endTimeDate,
          sendNotification: this.booking().sendNotification,
          roomId: this.booking().roomId || null,
        };

        // // Safely handle startTime
        // try {
        //   if (this.booking().bookingDate && this.booking().startTime) {
        //     const startTimeDate = new Date(`${this.booking().bookingDate} ${this.booking().startTime}`);
        //     if (!isNaN(startTimeDate.getTime())) {
        //       formValues.startTime = new Date(format(startTimeDate, 'yyyy-MM-dd HH:mm'));
        //     } else {
        //       formValues.startTime = new Date(); // Fallback to current time if invalid
        //     }
        //   } else {
        //     formValues.startTime = new Date(); // Fallback to current time if missing
        //   }
        // } catch (e) {
        //   console.error('Error parsing startTime:', e);
        //   formValues.startTime = new Date(); // Fallback to current time on error
        // }
        //
        // // Safely handle endTime
        // try {
        //   if (this.booking().bookingDate && this.booking().endTime) {
        //     const endTimeDate = new Date(`${this.booking().bookingDate} ${this.booking().endTime}`);
        //     if (!isNaN(endTimeDate.getTime())) {
        //       formValues.endTime = new Date(format(endTimeDate, 'yyyy-MM-dd HH:mm'));
        //     } else {
        //       // Calculate endTime based on startTime and duration if available
        //       if (formValues.startTime && this.booking().bookingService?.time) {
        //         const timeInMinutes = timeStrToSeconds(this.booking().bookingService.time);
        //         formValues.endTime = new Date(addMinutes(formValues.startTime, timeInMinutes));
        //       } else {
        //         formValues.endTime = new Date(addMinutes(formValues.startTime || new Date(), 30)); // Default 30 min
        //       }
        //     }
        //   } else {
        //     // Calculate endTime based on startTime and duration if available
        //     if (formValues.startTime && this.booking().bookingService?.time) {
        //       const timeInMinutes = timeStrToSeconds(this.booking().bookingService.time);
        //       formValues.endTime = new Date(addMinutes(formValues.startTime, timeInMinutes));
        //     } else {
        //       formValues.endTime = new Date(addMinutes(formValues.startTime || new Date(), 30)); // Default 30 min
        //     }
        //   }
        // } catch (e) {
        //   console.error('Error parsing endTime:', e);
        //   formValues.endTime = new Date(addMinutes(formValues.startTime || new Date(), 30)); // Default 30 min on error
        // }

        this.form.patchValue(formValues);

        // Set employee ID without triggering valueChanges
        this.form
          .get('employeeId')
          ?.setValue(this.booking().employeeId, { emitEvent: false });

        this.service.set(this.booking().bookingService?.service);

        // Set flag to true after initial booking data is loaded
        setTimeout(() => this.isFormInitialized.set(true), 0);
        if (
          this.booking().bookingService?.serviceProducts &&
          this.booking().bookingService?.serviceProducts?.length > 0
        ) {
          this.serviceProducts.set(
            this.mapServiceProducts(
              this.booking().bookingService.serviceProducts
            )
          );
        }
      } else {
        // Set initialized to true for non-editing case
        setTimeout(() => this.isFormInitialized.set(true), 0);
      }
    });
    this.form
      .get('startTime')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((startTime) => {
        console.log('Start time changed:', startTime);
        const time = this.form.get('time')?.value;

        // Validate startTime before using it
        if (time && startTime) {
          this.setEndTime(startTime, time);
        }
      });
    this.form
      ?.get('roomId')
      ?.valueChanges?.pipe(map((value) => value))
      ?.subscribe((res) => {
        console.log('Selected room ID changed:', res);
        this.selectedRoomId.set(res);
      });

    this.form
      .get('employeeId')
      ?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((employeeId) => {
        this.employeeId.set(employeeId);

        // beaut-239: surface a day-off / not-working warning as soon as the
        // employee is chosen, reusing the existing availability check + alert.
        const fv = this.form.value;
        if (
          canCheckAvailability({
            employeeId,
            bookingDate: fv.bookingDate ?? null,
            startTime: fv.startTime ?? null,
            endTime: fv.endTime ?? null,
          })
        ) {
          void this.checkAvailability();
        }

        const serviceId = this.form.get('serviceId')?.value;
        if (!this.isFormInitialized() || !serviceId || !employeeId) return;

        forkJoin({
          employeeServiceConfig: this.employeeServiceService
            .getByEmployeeAndService(employeeId, serviceId)
            .pipe(catchError(() => of(null))),
          employee: this.employeeService
            .getById(employeeId)
            .pipe(catchError(() => of(null))),
        })
          .pipe(untilDestroyed(this))
          .subscribe(({ employeeServiceConfig, employee }) => {
            if (employeeServiceConfig) {
              const time = employeeServiceConfig.time
                ? secondsToTimeStr(employeeServiceConfig.time)
                : this.service()?.time;
              this.form.patchValue({
                price: +(employeeServiceConfig.price ?? this.service()?.price),
                time,
                salaryFixed: employeeServiceConfig.fixedSalary,
                salaryPercent: employeeServiceConfig.outputPercent,
                salaryPercentFromPrice: false,
                minute:
                  employeeServiceConfig.time ||
                  timeStrToSeconds(this.service()?.time),
              });
            } else {
              this.form.patchValue({
                price: +(this.service()?.price || 0),
                time: this.service()?.time,
                salaryFixed: null,
                salaryPercent: employee?.outputPercent || null,
                minute: timeStrToSeconds(this.service()?.time || '00:00'),
              });
            }
          });
      });

    this.form
      .get('time')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((time) => {
        const startTime = this.form.get('startTime')?.value;

        // Validate both startTime and time before using them
        if (startTime && time) {
          try {
            // Validate time format before using it
            const timeInMinutes = timeStrToSeconds(time);

            if (timeInMinutes > 0) {
              this.setEndTime(startTime, time);

              if (this.service() && this.service().serviceType === 'MINUTE') {
                this.form.patchValue({
                  price: +this.service().price * +timeInMinutes,
                  salePrice: +this.service().discountPrice * +timeInMinutes,
                  minute: timeInMinutes,
                });
              }
            } else {
              console.error(
                'Invalid time duration (zero or negative minutes):',
                timeInMinutes
              );
            }
          } catch (e) {
            console.error('Error processing time value:', e);
          }
        }

        // beaut-239: re-evaluate the day-off warning when the time window shifts.
        const fv2 = this.form.value;
        if (
          canCheckAvailability({
            employeeId: fv2.employeeId ?? null,
            bookingDate: fv2.bookingDate ?? null,
            startTime: fv2.startTime ?? null,
            endTime: fv2.endTime ?? null,
          })
        ) {
          void this.checkAvailability();
        }
      });

    effect(() => {
      if (this.employee() && !this.booking()) {
        console.log(
          'Employee data available for new booking:',
          this.employee()
        );
        // For new bookings, initialize with employee data from facade
        // Set values directly to avoid triggering valueChanges

        if (
          this.service() &&
          this.service().employeeServices &&
          this.service().employeeServices.length > 0
        ) {
          const employeeService = this.service().employeeServices.find(
            (es: any) => es.employeeId === this.employee().id
          );
          if (employeeService) {
            this.form.patchValue({
              salaryFixed: employeeService.fixedSalary,
              salaryPercent: employeeService.outputPercent,
              salaryPercentFromPrice: false,
              minute:
                employeeService.time || timeStrToSeconds(this.service().time),
            });
          } else {
            this.form.patchValue(
              {
                salaryFixed: null,
                employeeId: this.employee().id,
                salaryPercent: this.employee().outputPercent,
              },
              { emitEvent: false }
            );
          }
        } else {
          this.form.patchValue(
            {
              salaryFixed: null,
              employeeId: this.employee().id,
              salaryPercent: this.employee().outputPercent,
            },
            { emitEvent: false }
          );
        }
      }
    });

    // Fetch available rooms when component initializes
    effect(() => {
      const bookingDate = this.data.bookingDate;
      if (bookingDate) {
        this.fetchAvailableRooms(bookingDate);
      }
    });
  }

  ngOnInit() {
    if (this.data.roomId) {
      this.form.patchValue({ roomId: this.data.roomId });
      this.selectedRoomId.set(this.data.roomId);
    }
  }

  /**
   * Fetches available rooms for the given booking date
   */
  fetchAvailableRooms(date: string) {
    this.roomService
      .getRoomsForCalendar({ date })
      .pipe(untilDestroyed(this))
      .subscribe((rooms) => {
        this.availableRooms.set(
          rooms.map((room) => ({
            id: room.id,
            name: room.name,
            description: room.description,
            canWorkMultipleEmployees: room.canWorkMultipleEmployees,
            maxParallelBookings: room.maxParallelBookings,
          }))
        );
      });
  }

  /**
   * Fetches service details by ID
   */
  getServiceById(serviceId: any) {
    this.serviceService
      .getById(serviceId)
      .pipe(
        untilDestroyed(this),
        map((res: any) => {
          this.service.set(res);
          if (res.serviceProducts && res.serviceProducts.length > 0) {
            this.serviceProducts.set(
              this.mapServiceProducts(res.serviceProducts)
            );
          }

          return res;
        })
      )
      .subscribe();
  }

  /**
   * Calculates and sets the end time based on start time and duration
   * @param startTime The starting time
   * @param time The duration in format "HH:MM"
   */
  setEndTime(startTime: Date, time: string) {
    try {
      // Validate inputs
      if (!startTime) {
        console.error('Invalid startTime provided to setEndTime:', startTime);
        return; // Don't update if startTime is invalid
      }

      if (!time) {
        console.error('Invalid time duration provided to setEndTime');
        return; // Don't update if time is invalid
      }

      const timeInMinutes = timeStrToSeconds(time);

      if (timeInMinutes <= 0) {
        console.error(
          'Invalid time duration (zero or negative minutes):',
          timeInMinutes
        );
        return; // Don't update if duration is invalid
      }

      // Create a new Date object to avoid modifying the original
      const startTimeDate = new Date(startTime);

      // Calculate end time
      const endTime = new Date(addMinutes(startTimeDate, timeInMinutes));

      // Validate the result
      if (!endTime) {
        console.error('Invalid endTime calculated:', endTime);
        return; // Don't update if result is invalid
      }

      // Update the form
      this.form.patchValue({
        endTime: new Date(endTime),
      });
    } catch (e) {
      console.error('Error in setEndTime:', e);
      // Don't update the form on error
    }
  }

  setEmployeeServiceInfo(employeeId: string) {
    if (
      this.service() &&
      this.service().employeeServices &&
      this.service().employeeServices.length > 0
    ) {
      const employeeService = this.service().employeeServices.find(
        (es: any) => es.employeeId === employeeId
      );
      if (employeeService) {
        this.form.patchValue({
          salaryFixed: employeeService.fixedSalary,
          salaryPercent: employeeService.outputPercent,
          salaryPercentFromPrice: false,
          minute: employeeService.time || timeStrToSeconds(this.service().time),
        });
      }
    }
  }

  /**
   * Handles service selection
   */
  async chooseService(service: any) {
    if (!service) {
      console.error('Invalid service provided to chooseService');
      return;
    }
    let time = service.time;
    let price = service.price;
    if (this.employee()) {
      console.log('Employee service:', this.employee(), service.id);

      let employeeService = null;
      if (service.employeeServices?.length > 0) {
        employeeService = service.employeeServices.find(
          (es: any) => es.employeeId === this.employee().id
        );
      }
      console.log('Employee service:', employeeService);

      this.needChooseService.set(false);

      if (employeeService) {
        this.form.patchValue({
          salaryFixed: employeeService.fixedSalary,
          salaryPercent: employeeService.outputPercent,
          salaryPercentFromPrice: false,
          minute: employeeService.time || timeStrToSeconds(service.time),
        });
        if (employeeService.time) {
          console.log(
            'Employee service time:',
            secondsToTimeStr(employeeService.time)
          );
          time = secondsToTimeStr(employeeService.time);
        }
        if (employeeService.price) {
          console.log('Employee service price:', employeeService.price);
          price = +employeeService.price;
        }
      } else {
        this.form.patchValue({
          salaryFixed: null,
          salaryPercent: this.employee().outputPercent,
          salaryPercentFromPrice: false,
          minute: timeStrToSeconds(service.time),
        });
      }
    } else {
      this.form.patchValue({
        minute: timeStrToSeconds(service.time),
      });
      this.needChooseService.set(false);
    }
    this.service.set(service);

    this.form.patchValue({
      serviceId: service.id,
      time: service.autoTimeView ? time : '00:00',
      price: service.autoPriceView ? price : null,
      salePrice:
        service.autoPriceView && +service.discountPrice > 0
          ? +service.discountPrice
          : null,
    });

    const startTime = this.form.get('startTime')?.value;

    // Only auto-calculate endTime if autoTimeView is enabled
    if (
      service.autoTimeView &&
      startTime &&
      startTime instanceof Date &&
      !isNaN(startTime.getTime()) &&
      time &&
      typeof time === 'string' &&
      (time as string).trim() !== ''
    ) {
      try {
        // Validate time format
        const timeInMinutes = timeStrToSeconds(time);
        if (timeInMinutes > 0) {
          this.setEndTime(startTime, time);
        } else {
          console.error(
            'Invalid service time duration (zero or negative minutes):',
            timeInMinutes
          );
        }
      } catch (e) {
        console.error('Error processing service time:', e);
      }
    }

    this.serviceProducts.set(
      this.mapServiceProducts(service.serviceProducts || [])
    );

    // autoFix off → expense 0; autoFix+fixSum → fixSum; autoFix alone → products.
    // Do not touch additionalExpense — it is manual only (leave form value as-is).
    this.form.patchValue({
      expense: seedAutoFixExpense(
        service.autoFix,
        service.fixSum,
        this.calculateTotalExpense()
      ),
    });
  }

  /**
   * Handles offer service selection from the offers tab
   * Fetches full service details and passes to chooseService with offer metadata
   */
  chooseOfferService(offerService: AvailableOfferService) {
    if (!offerService) {
      console.error('Invalid offer service provided');
      return;
    }

    // Fetch full service details
    this.serviceService
      .getById(offerService.serviceId)
      .pipe(untilDestroyed(this))
      .subscribe((service) => {
        // Set the service with offer data
        // Override price with 0 since it's prepaid
        this.chooseService({
          ...service,
          // Store offer metadata for booking
          offerPurchaseId: offerService.purchaseId,
          offerServiceId: offerService.offerServiceId,
          // The actual price from the offer (for display purposes)
          offerOriginalPrice: offerService.originalPrice,
          offerDiscountedPrice: offerService.discountedPrice,
        });

        // After chooseService sets the price, override with 0 (prepaid)
        this.form.patchValue({
          price: 0,
          salePrice: 0,
        });
      });
  }

  /**
   * Handles package service selection from the packages tab
   * Fetches full service details and passes to chooseService with package metadata
   */
  choosePackageService(packageService: AvailablePackageService) {
    if (!packageService) return;

    this.serviceService
      .getById(packageService.serviceId)
      .pipe(untilDestroyed(this))
      .subscribe((service) => {
        this.chooseService({
          ...service,
          packagePurchaseId: packageService.purchaseId,
          packageItemId: packageService.packageItemId,
          packageOriginalPrice: packageService.originalPrice,
          packageDiscountedPrice: packageService.discountedPrice,
        });

        // Keep package prices for display (total calculation excludes package services separately)
        this.form.patchValue({
          price: packageService.originalPrice,
          salePrice: packageService.discountedPrice,
        });
      });
  }

  /**
   * Prepares booking data from form values
   */
  private prepareBookingData() {
    const {
      visitId,
      price,
      salePrice,
      salaryFixed,
      salaryPercent,
      salaryPercentFromPrice,
      expense,
      additionalExpense,
      quantity,
      serviceId,
      time,
      startTime,
      endTime,
      description,
      employeeId,
      roomId,
      sendNotification,
      bookingDate,
      additionalEmployees,
    } = this.form.value;

    // Get offer data from service if it's from an offer
    const service = this.service();
    const offerPurchaseId = service?.offerPurchaseId || null;
    const offerServiceId = service?.offerServiceId || null;
    const packagePurchaseId = service?.packagePurchaseId || null;
    const packageItemId = service?.packageItemId || null;

    return {
      visitId,
      employeeId,
      roomId, // Include room assignment
      serviceId,
      price,
      discountPrice: salePrice || null,
      salaryFixed,
      salaryPercent,
      salaryPercentFromPrice,
      expence: expense,
      additionalExpence: +(additionalExpense || 0),
      quantity,
      time,
      startTime,
      endTime,
      description,
      specServices: this.serviceSpecs(),
      serviceProducts: this.serviceProducts(),
      sendNotification,
      offerPurchaseId,
      offerServiceId,
      packagePurchaseId,
      packageItemId,
      bookingDate,
      additionalEmployees,
    };
  }

  /**
   * Checks availability for employee, room, and additional employees
   * Returns true if available, false if there are conflicts
   */
  async checkAvailability(): Promise<boolean> {
    const { employeeId, roomId, startTime, endTime, bookingDate } =
      this.form.value;

    if (!employeeId || !startTime || !endTime || !bookingDate) {
      return true; // Skip check if required fields are missing
    }

    this.isCheckingAvailability.set(true);
    this.availabilityConflicts.set([]);

    try {
      const serviceId = this.form.get('serviceId')?.value;
      const params: CheckAvailabilityRequest = {
        employeeId,
        date:
          typeof bookingDate === 'string'
            ? bookingDate
            : format(bookingDate, 'yyyy-MM-dd'),
        startTime: format(startTime, 'HH:mm'),
        endTime: format(endTime, 'HH:mm'),
        roomId: roomId || undefined,
        serviceId: serviceId || undefined,
        additionalEmployees:
          this.additionalEmployeesFrom.value
            ?.filter((e: any) => e.employeeId)
            ?.map((e: any) => ({ employeeId: e.employeeId })) || undefined,
        excludeBookingId: this.booking()?.id || undefined,
        force: this.proceedDespiteConflicts(),
      };

      const response = await lastValueFrom(
        this.bookingService.checkAvailability(params).pipe(
          catchError((error) => {
            console.error('Availability check failed:', error);
            return of({
              available: true,
              conflicts: [],
            } as AvailabilityResponse);
          })
        )
      );

      this.availabilityConflicts.set(response.conflicts);
      return response.available;
    } finally {
      this.isCheckingAvailability.set(false);
    }
  }

  /**
   * Gets formatted conflict message for display
   */
  getConflictMessage(conflict: AvailabilityConflict): string {
    if (conflict.reason) {
      return `${conflict.name}: ${conflict.reason}`;
    }
    if (conflict.conflictingBooking) {
      return `${conflict.name}: კონფლიქტი ჯავშანთან ${
        conflict.conflictingBooking.startTime
      }-${conflict.conflictingBooking.endTime}${
        conflict.conflictingBooking.serviceName
          ? ` (${conflict.conflictingBooking.serviceName})`
          : ''
      }`;
    }
    return conflict.name;
  }

  /**
   * Whether any conflicts are hard (cannot be overridden)
   */
  hasHardConflicts = computed(() =>
    this.availabilityConflicts().some((c) => c.severity === 'hard')
  );

  /**
   * Confirms proceeding despite availability conflicts (only soft conflicts can be overridden)
   */
  confirmDespiteConflicts() {
    this.proceedDespiteConflicts.set(true);
    this.submit();
  }

  /**
   * Handles errors and resets loading state
   */
  private handleError(error: any) {
    this.loading.set(false);
    return throwError(() => error);
  }

  /**
   * Handles successful operations
   */
  private handleSuccess() {
    this.modalRef.close();
    this.loading.set(false);
  }

  /**
   * Handles form submission for creating or updating a booking
   */
  async submit() {
    if (this.loading()) return;

    this.form.markAllAsTouched();
    console.log('Form submitted:', this.form);

    if (this.form.invalid) return;

    // If user hasn't acknowledged conflicts yet, check availability first
    if (!this.proceedDespiteConflicts()) {
      const isAvailable = await this.checkAvailability();
      if (!isAvailable) {
        // Conflicts found - show warning and wait for user to confirm
        return;
      }
    }

    this.loading.set(true);

    const bookingData = this.prepareBookingData();

    console.log('bookingData:', bookingData);

    if (this.data.visitId) {
      this.visitAddFacade
        .createVisitBooking({
          ...bookingData,
          bookingDate: this.data.bookingDate,
        })
        .pipe(
          untilDestroyed(this),
          catchError((error) => this.handleError(error))
        )
        .subscribe(() => this.handleSuccess());
    } else {
      this.modalRef.close({
        employee: this.employee(),
        roomId: bookingData.roomId, // Include room assignment
        room: this.selectedRoom(),
        totalLastPrice: bookingData.discountPrice ?? bookingData.price ?? 0,
        salePrice: bookingData.discountPrice ?? null,
        bookingDate: bookingData.bookingDate,
        startTimeDate: bookingData.startTime,
        endTimeDate: bookingData.endTime,
        startTime: bookingData.startTime
          ? format(bookingData.startTime, 'HH:mm')
          : null,
        endTime: bookingData.endTime
          ? format(bookingData.endTime, 'HH:mm')
          : null,
        description: bookingData.description,
        employeeId: this.employee().id,
        bookingServices: [
          {
            price: bookingData.price ?? 0,
            salePrice: bookingData.discountPrice ?? null,
            lastPrice: bookingData.discountPrice ?? bookingData.price ?? 0,
            time: bookingData.time ?? '',
            // beaut-609: never `|| 0`. For an operator who may not see salaries
            // the value arrives empty; `|| 0` turned it into a deliberate 0 that
            // the server keeps, so the procedure saved at 0% instead of the
            // employee's default. Empty stays empty; salaryForRequest then drops
            // the key and the server applies the default. A typed 0 is kept.
            salaryFixed: bookingData.salaryFixed ?? null,
            salaryPercent: bookingData.salaryPercent ?? null,
            salaryPercentFromPrice: bookingData.salaryPercentFromPrice ?? false,
            expence: bookingData.expence ?? 0,
            // beaut-240: without this the new-visit draft posts additionalExpence: 0
            // no matter what the user typed. visit-add-booking forwards the field
            // faithfully, but nothing was putting it into the draft.
            additionalExpence: bookingData.additionalExpence ?? 0,
            quantity: bookingData.quantity ?? 1,
            serviceId: bookingData.serviceId,
            employeeId: this.employee().id,
            employee: this.employee(),
            service: this.service(),
            serviceProducts: this.serviceProducts(),
            specServices: this.serviceSpecs(),
            description: bookingData.description,
            additionalEmployees: bookingData.additionalEmployees,
            offerPurchaseId: bookingData.offerPurchaseId,
            offerServiceId: bookingData.offerServiceId,
            packagePurchaseId: bookingData.packagePurchaseId,
            packageItemId: bookingData.packageItemId,
          },
        ] as unknown as BookingService[],
      } as unknown as AddVisitBooking);
    }
  }

  /**
   * Maps service products to include calculated price
   */
  private mapServiceProducts(products: any[]): any[] {
    return (
      products?.map((m: any) => {
        let price = 0;
        if (m.product) {
          if (m.quantitySum > 0) {
            price = +m.product.salePricePerItem * +m.quantitySum;
          }
          if (m.grSum > 0) {
            price = +(
              (+m.product.salePricePerItem / +m.product.grPerItem) *
              +m.grSum
            ).toFixed(2);
          }
        } else {
          price = +m.priceValue || 0;
        }
        return {
          ...m,
          productId: m.product?.id || m.productId,
          productName: m.product?.name || m.productName || null,
          priceValue: price,
          price,
        };
      }) || []
    );
  }

  /** beaut-240: product cost + additional cost, mirroring BookingServiceForm. */
  get totalExpense(): number {
    const productExpense = +(this.form.get('expense')?.value || 0);
    const additionalExpense = +(this.form.get('additionalExpense')?.value || 0);
    return productExpense + additionalExpense;
  }

  /**
   * Calculates total expense from service products
   */
  private calculateTotalExpense(): number {
    return this.serviceProducts().reduce((previousValue, currentValue) => {
      return (
        previousValue + (+currentValue.price || +currentValue.priceValue || 0)
      );
    }, 0);
  }

  serviceProductsUpdate($event: any[]) {
    this.serviceProducts.set($event);

    // Only refresh expense from products when autoFix is on and fixSum is empty.
    // A fixed fixSum must not be overwritten by recipe edits.
    if (shouldTrackProductExpense(this.service()?.autoFix, this.service()?.fixSum)) {
      this.form.patchValue({
        expense: this.calculateTotalExpense(),
      });
    }
  }

  serviceSpecsUpdate($event: any[]) {
    this.serviceSpecs.set($event);
  }

  changePercentage($event: any) {
    if (!$event) {
      this.form.patchValue({
        salePrice: null,
      });
      return;
    }
    const { price } = this.form.value;
    this.form.patchValue({
      salePrice:
        price && price > 0
          ? +(price - (price * $event) / 100).toFixed(2)
          : null,
    });
  }

  /**
   * Adds a new additional employee to the list
   */
  addAdditionalEmployee() {
    console.log('Adding additional employee');
    this.additionalEmployeesFrom.push(
      new FormGroup({
        employee: new FormControl(),
        employeeId: new FormControl(),
        salaryFixed: new FormControl(),
        // beaut-424: a share of a price cannot exceed the price. The backend
        // rejects >100 with a 422; without this the operator only discovers that
        // at final save, as a status code instead of a message under the field.
        salaryPercent: new FormControl(null, [
          Validators.min(0),
          Validators.max(100),
        ]),
      })
    );
  }

  /**
   * Removes an additional employee from the list
   * @param index The index of the employee to remove
   */
  removeAdditionalEmployee(index: number) {
    this.additionalEmployeesFrom.removeAt(index);
  }

  /**
   * Handles employee selection for additional employees
   * Fetches employee data and sets salary percent
   * @param index The index of the employee to update
   * @param employeeId The selected employee ID
   */
  onAdditionalEmployeeChange(index: number, employeeId: string) {
    if (!employeeId) return;

    // Fetch employee details
    this.employeeService
      .getById(employeeId)
      .pipe(untilDestroyed(this))
      .subscribe((employee) => {
        // Get the form group at the specified index
        const employeeFormGroup = this.additionalEmployeesFrom.at(
          index
        ) as FormGroup;

        if (employeeFormGroup) {
          // Set the salary percent from the employee's outputPercent
          employeeFormGroup.patchValue({
            employee,
            salaryPercent: employee.outputPercent || null,
            salaryFixed: null,
          });
        }
      });
  }
}
